// ═══════════════════════════════════════════════════════════════════════════
//  AsistenteCmsTab.jsx — CRM de la base de conocimiento del Asistente Spacy (Fase 3)
//
//  Aquí se cargan los documentos extensos (alumnos, empresas, exámenes, curso,
//  FAQs) que el asistente CITA al responder. El asistente vale lo que vale su
//  base. Backend: /api/admin/asistente.
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import './AsistenteCmsTab.css';

function cab() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };
}
const API = '/api/admin/asistente';
const AUD_LABEL = { alumno: '🎓 Alumno', empresa: '🏢 Empresa', general: '🌐 General' };

const VACIO = { id: null, slug: '', titulo: '', audiencia: 'general', seccion: '', contenido: '', tags: [], activo: true };

function loQueDijo(d) {
  if (!d) return 'Ocurrió un error.';
  if (typeof d.detail === 'string') return d.detail;
  if (Array.isArray(d.detail)) return d.detail.map((e) => e.msg || '').join(' · ') || 'Datos inválidos.';
  return d.message || 'Ocurrió un error.';
}

export default function AsistenteCmsTab() {
  const [salud, setSalud] = useState(null);
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [filtros, setFiltros] = useState({ audiencia: '', seccion: '', activo: '', buscar: '' });
  const [editando, setEditando] = useState(null);
  const [vista, setVista] = useState('base');   // 'base' | 'obs'

  const recargaSalud = useCallback(() => {
    fetch(`${API}/salud`, { headers: cab() }).then((r) => r.json()).then(setSalud).catch(() => {});
  }, []);
  useEffect(() => { recargaSalud(); }, [recargaSalud]);

  const carga = useCallback(async () => {
    setCargando(true);
    const p = new URLSearchParams();
    if (filtros.audiencia) p.set('audiencia', filtros.audiencia);
    if (filtros.seccion) p.set('seccion', filtros.seccion);
    if (filtros.activo !== '') p.set('activo', filtros.activo);
    if (filtros.buscar) p.set('buscar', filtros.buscar);
    p.set('limite', '200');
    try {
      const r = await fetch(`${API}/docs?${p}`, { headers: cab() });
      const d = await r.json();
      setDocs(d.docs || []); setTotal(d.total || 0);
    } catch { setAviso({ tipo: 'error', txt: 'No se pudo cargar la base.' }); }
    setCargando(false);
  }, [filtros]);
  useEffect(() => { carga(); }, [carga]);

  async function guarda() {
    const nuevo = !editando.id;
    const cuerpo = {
      slug: editando.slug.trim(), titulo: editando.titulo, audiencia: editando.audiencia,
      seccion: editando.seccion || null, contenido: editando.contenido, tags: editando.tags, activo: editando.activo,
    };
    try {
      const r = await fetch(nuevo ? `${API}/docs` : `${API}/docs/${editando.id}`,
        { method: nuevo ? 'POST' : 'PUT', headers: cab(), body: JSON.stringify(cuerpo) });
      const d = await r.json();
      if (!r.ok) { setAviso({ tipo: 'error', txt: loQueDijo(d) }); return; }
      setAviso({ tipo: 'ok', txt: nuevo ? 'Documento creado.' : 'Documento actualizado.' });
      setEditando(null); carga(); recargaSalud();
    } catch { setAviso({ tipo: 'error', txt: 'Fallo de conexión al guardar.' }); }
  }

  async function regeneraBiblioteca() {
    if (!window.confirm('El bibliotecario regenerará las fichas automáticas (planes, precios, '
      + 'niveles y FAQs) desde los datos vivos de NSL. Tus documentos escritos a mano NO se tocan. '
      + '¿Continuar?')) return;
    try {
      const r = await fetch(`${API}/biblioteca/regenerar`, { method: 'POST', headers: cab() });
      const d = await r.json();
      if (!r.ok) { setAviso({ tipo: 'error', txt: loQueDijo(d) }); return; }
      setAviso({ tipo: 'ok', txt: `Biblioteca al día: ${d.creados} nuevas, ${d.actualizados} actualizadas, ${d.podados} podadas · ${d.total_activos} activas.` });
      carga(); recargaSalud();
    } catch { setAviso({ tipo: 'error', txt: 'Fallo de conexión al regenerar la biblioteca.' }); }
  }

  async function desactiva(doc) {
    if (!window.confirm(`¿Desactivar "${doc.titulo}"? El asistente dejará de citarlo.`)) return;
    try {
      const r = await fetch(`${API}/docs/${doc.id}`, { method: 'DELETE', headers: cab() });
      const d = await r.json();
      if (!r.ok) { setAviso({ tipo: 'error', txt: loQueDijo(d) }); return; }
      setAviso({ tipo: 'ok', txt: `"${doc.titulo}" desactivado.` }); carga(); recargaSalud();
    } catch { setAviso({ tipo: 'error', txt: 'Fallo de conexión.' }); }
  }

  return (
    <div className="acms">
      <header className="acms-head">
        <div>
          <h2>Asistente Spacy · Base de Conocimiento</h2>
          <p className="acms-sub">
            Los documentos que Spacy cita al responder al alumno. El asistente
            vale lo que vale su base: si no está aquí, no lo sabe.
          </p>
        </div>
        {vista === 'base' && (
          <div className="acms-head-acc">
            <button className="acms-btn" onClick={regeneraBiblioteca} title="Cosecha planes, precios y niveles reales de NSL a la base de Spacy">🔄 Regenerar biblioteca</button>
            <button className="acms-btn acms-btn-primario" onClick={() => setEditando({ ...VACIO })}>+ Nuevo documento</button>
          </div>
        )}
      </header>

      <div className="acms-vista">
        <button className={`acms-vista-btn ${vista === 'base' ? 'acms-vista-on' : ''}`} onClick={() => setVista('base')}>Base de conocimiento</button>
        <button className={`acms-vista-btn ${vista === 'obs' ? 'acms-vista-on' : ''}`} onClick={() => setVista('obs')}>Observabilidad</button>
      </div>

      {aviso && (
        <div className={`acms-aviso acms-aviso-${aviso.tipo}`} onClick={() => setAviso(null)}>
          {aviso.txt} <span className="acms-x">✕</span>
        </div>
      )}

      {vista === 'obs' && <Observabilidad />}

      {vista === 'base' && salud && (
        <div className="acms-salud">
          <span><b>{salud.total}</b> documentos</span>
          <span><b>{salud.activos}</b> activos</span>
          {Object.entries(salud.por_audiencia || {}).map(([a, n]) => (
            <span key={a}>{AUD_LABEL[a] || a}: <b>{n}</b></span>
          ))}
        </div>
      )}

      {vista === 'base' && (<>
      <div className="acms-filtros">
        <input className="acms-inp" placeholder="Buscar por slug o título…" value={filtros.buscar}
               onChange={(e) => setFiltros((f) => ({ ...f, buscar: e.target.value }))} />
        <select className="acms-inp" value={filtros.audiencia} onChange={(e) => setFiltros((f) => ({ ...f, audiencia: e.target.value }))}>
          <option value="">Toda audiencia</option>
          <option value="alumno">Alumno</option>
          <option value="empresa">Empresa</option>
          <option value="general">General</option>
        </select>
        <select className="acms-inp" value={filtros.activo} onChange={(e) => setFiltros((f) => ({ ...f, activo: e.target.value }))}>
          <option value="">Activos e inactivos</option>
          <option value="true">Solo activos</option>
          <option value="false">Solo inactivos</option>
        </select>
        <span className="acms-total">{total} documentos</span>
      </div>

      {cargando ? <div className="acms-vacio">Cargando…</div>
        : !docs.length ? <div className="acms-vacio">No hay documentos con esos filtros. Crea el primero.</div>
        : (
          <div className="acms-tabla-wrap">
            <table className="acms-tabla">
              <thead><tr><th>Slug</th><th>Título</th><th>Audiencia</th><th>Sección</th><th></th></tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className={d.activo ? '' : 'acms-off'}>
                    <td className="acms-slug">{d.slug}</td>
                    <td>{d.titulo}</td>
                    <td>{AUD_LABEL[d.audiencia] || d.audiencia}</td>
                    <td className="acms-dim">{d.seccion || '—'}</td>
                    <td className="acms-acc">
                      <button className="acms-mini" onClick={() => setEditando(JSON.parse(JSON.stringify(d)))}>Editar</button>
                      {d.activo && <button className="acms-mini acms-mini-off" onClick={() => desactiva(d)}>Desactivar</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>)}

      {editando && (
        <Editor editando={editando} setEditando={setEditando}
                onGuardar={guarda} onCerrar={() => setEditando(null)} />
      )}
    </div>
  );
}

// ── Vista de observabilidad del asistente ─────────────────────────────────
function Observabilidad() {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    fetch(`${API}/observabilidad`, { headers: cab() })
      .then((r) => r.json()).then((d) => { setData(d); setCargando(false); })
      .catch(() => setCargando(false));
  }, []);
  if (cargando) return <div className="acms-vacio">Cargando…</div>;
  if (!data) return <div className="acms-vacio">Sin datos.</div>;
  const r = data.resumen || {};
  const cards = [
    ['Conversaciones atendidas', r.conversaciones_atendidas],
    ['Respuestas de Spacy', r.respuestas_de_spacy],
    ['Con fundamento (base)', r.con_fundamento],
    ['Personales (tokens/clase)', r.personales],
    ['Sin base (ofreció humano)', r.sin_base],
    ['Acciones 🟡 ejecutadas', r.acciones_ejecutadas],
    ['Escaladas a humano', r.escaladas_a_humano],
    ['Tasa de escalación', `${r.tasa_escalacion ?? 0}%`],
  ];
  return (
    <div className="acms-obs">
      <div className="acms-obs-cards">
        {cards.map(([lbl, val]) => (
          <div key={lbl} className="acms-obs-card">
            <div className="acms-obs-num">{val ?? 0}</div>
            <div className="acms-obs-lbl">{lbl}</div>
          </div>
        ))}
      </div>
      <h3 className="acms-obs-tit">Últimas preguntas de alumnos</h3>
      {(data.preguntas_recientes || []).length === 0 ? (
        <div className="acms-vacio">Aún no hay preguntas.</div>
      ) : (
        <div className="acms-tabla-wrap">
          <table className="acms-tabla">
            <thead><tr><th>Alumno</th><th>Pregunta</th></tr></thead>
            <tbody>
              {data.preguntas_recientes.map((p, i) => (
                <tr key={i}>
                  <td className="acms-dim">{p.alumno}</td>
                  <td>{p.pregunta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Editor({ editando, setEditando, onGuardar, onCerrar }) {
  const set = (k, v) => setEditando((e) => ({ ...e, [k]: v }));
  const largo = (editando.contenido || '').length;
  return (
    <div className="acms-modal" onClick={onCerrar}>
      <div className="acms-editor" onClick={(e) => e.stopPropagation()}>
        <h3>{editando.id ? `Editar ${editando.slug}` : 'Nuevo documento'}</h3>
        <div className="acms-row2">
          <label className="acms-lbl">Slug único
            <input className="acms-inp" value={editando.slug} maxLength={64} placeholder="faq-tokens"
                   disabled={!!editando.id} onChange={(e) => set('slug', e.target.value)} />
          </label>
          <label className="acms-lbl">Audiencia
            <select className="acms-inp" value={editando.audiencia} onChange={(e) => set('audiencia', e.target.value)}>
              <option value="general">General</option>
              <option value="alumno">Alumno</option>
              <option value="empresa">Empresa</option>
            </select>
          </label>
        </div>
        <div className="acms-row2">
          <label className="acms-lbl">Título
            <input className="acms-inp" value={editando.titulo} maxLength={200} onChange={(e) => set('titulo', e.target.value)} />
          </label>
          <label className="acms-lbl">Sección (opcional)
            <input className="acms-inp" value={editando.seccion || ''} maxLength={80} placeholder="Exámenes, Curso…"
                   onChange={(e) => set('seccion', e.target.value)} />
          </label>
        </div>
        <label className="acms-lbl">Contenido — lo que Spacy citará ({largo}/20000)
          <textarea className="acms-inp acms-area" value={editando.contenido} maxLength={20000}
                    onChange={(e) => set('contenido', e.target.value)} />
        </label>
        <label className="acms-lbl">Etiquetas (separadas por coma) — ayudan a que Spacy lo encuentre
          <input className="acms-inp" value={(editando.tags || []).join(', ')}
                 onChange={(e) => set('tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))} />
        </label>
        <label className="acms-lbl acms-check">
          <input type="checkbox" checked={editando.activo} onChange={(e) => set('activo', e.target.checked)} />
          Activo (el asistente puede citarlo)
        </label>
        <div className="acms-botones">
          <button className="acms-btn" onClick={onCerrar}>Cancelar</button>
          <button className="acms-btn acms-btn-primario" onClick={onGuardar}>Guardar</button>
        </div>
      </div>
    </div>
  );
}
